import { useState } from "react";
import { ArrowLeft, ArrowRight, Camera, CheckCircle, WarningCircle, Warning, List, Ruler, Barcode, Wrench, Trash, Prohibit, NotePencil, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { RATING, roomPhotos, roomLabel, photoUrl } from "./walkApi";
import ItemIcon from "./itemIcons";
import CalloutEditor from "./CalloutEditor";


function Chip({ it, selected, onTap, photos, needOverride }) {
  const r = it.rating && RATING[it.rating];
  const need = (it.rating === "fair" || it.rating === "dmg") && needOverride;
  // eslint-disable-next-line no-unused-vars
  const _ = photos;
  return (
    <button onClick={onTap} data-testid={`walk-item-${it.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
      className={`relative flex items-center gap-2 px-2.5 h-[72px] rounded-2xl border text-left transition-colors
        ${selected ? "bg-[#1b3a55] border-teal-400 ring-2 ring-teal-400" : "bg-[#13263a] border-white/10 active:bg-[#1a3149]"}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0
        ${it.rating === "good" ? "bg-emerald-500/25 text-emerald-300" : it.rating === "fair" ? "bg-yellow-400/25 text-yellow-300" : it.rating === "dmg" ? "bg-red-500/25 text-red-300" : it.rating === "na" ? "bg-slate-500/25 text-slate-400" : "bg-white/10 text-slate-200"}`}>
        <ItemIcon name={it.name} size={22}/>
      </div>
      <div className="min-w-0 flex-1">
        <div className={`font-bold leading-tight ${r ? "text-[14px]" : "text-[16px]"}`}>{it.name}</div>
        {(it.measurable || it.appliance) && (
          <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
            {it.measurable && <><Ruler size={11}/> tape</>}{it.appliance && <><Barcode size={11}/> nameplate</>}
          </div>
        )}
      </div>
      {r && (
        <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-black flex items-center gap-0.5 shrink-0 ${r.bg}`}>
          {it.rating === "good" ? <CheckCircle weight="fill" size={12}/> : it.rating === "na" ? <Prohibit weight="bold" size={12}/> : <WarningCircle weight="fill" size={12}/>} {r.label}
        </span>
      )}
      {photos > 0 && !need && <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full bg-white/15 text-[9px] font-black flex items-center gap-0.5"><Camera weight="fill" size={9}/>{photos}</span>}
      {need && <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full bg-red-600 text-[9px] font-black uppercase">photo</span>}
      {it.wo_id && <span className="absolute -top-1.5 left-2 px-1.5 py-0.5 rounded-full bg-teal-500 text-slate-900 text-[9px] font-black uppercase">WO</span>}
    </button>
  );
}

export default function RoomScreen({ data, roomIdx, mode, staff, onBack, onNext, onOpenCamera, onRate, onMeasure, onNameplate, onFastFlag, onFindingWo, onMenu, onDeletePhoto, onNote, onCallouts }) {
  const [viewing, setViewing] = useState(null);
  const [marking, setMarking] = useState(null);
  const [note, setNote] = useState(data.rooms[roomIdx].note || "");
  const [showNote, setShowNote] = useState(!!data.rooms[roomIdx].note);
  const rooms = data.rooms;
  const room = rooms[roomIdx];
  const photos = roomPhotos(room);
  const [sel, setSel] = useState(null);
  const [flag, setFlag] = useState(room.fast_flag || "");
  const untagged = photos.filter(p => !p.item).length;
  const itemPhotos = (name) => photos.filter(p => p.item === name).length;
  const hasEvidence = (name) => itemPhotos(name) + untagged > 0;
  const selItem = room.items.find(i => i.name === sel);
  const unphotographed = room.items.filter(i => (i.rating === "fair" || i.rating === "dmg") && !hasEvidence(i.name));

  const rate = async (rating) => {
    if (!sel) { toast("Tap an item first, then Good / Fair / Damage", { duration: 1600 }); return; }
    await onRate(sel, rating);
    if ((rating === "fair" || rating === "dmg") && !hasEvidence(sel)) {
      toast.warning(`${rating === "fair" ? "Fair" : "Damage"} needs a photo — opening camera`, { duration: 1500 });
      onOpenCamera(sel);
      return;
    }
    if (selItem?.measurable && (rating === "fair" || rating === "dmg")) onMeasure(sel);
  };

  const next = () => {
    if (mode === "guided" && unphotographed.length) {
      if (staff && window.confirm(`Photo needed for: ${unphotographed.map(i => i.name).join(", ")}.\n\nComplete this room anyway (override)?`)) {
        onNext(true);
        return;
      }
      toast.error(`Photo needed: ${unphotographed.map(i => i.name).join(", ")}`);
      return;
    }
    onNext(false);
  };

  return (
    <div className="min-h-screen bg-[#0b1c2d] text-white pb-32" data-testid="walk-room-screen">
      <div className="sticky top-0 z-20 bg-[#0b1c2d]/95 backdrop-blur px-4 pt-3 pb-3 flex items-center gap-2">
        <button onClick={onBack} className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full active:bg-white/10" data-testid="walk-room-back">
          <ArrowLeft weight="bold" size={26} className="text-teal-400"/>
        </button>
        <div className="min-w-0 flex-1">
          <div className="font-black text-[22px] leading-tight truncate" data-testid="walk-room-title">
            {room.name}{room.custom_name && <span className="text-teal-400"> · </span>}{room.custom_name}
          </div>
          <div className="text-sm text-slate-400 flex items-center gap-1.5" data-testid="walk-room-progress">
            {roomIdx + 1} of {rooms.length} rooms {room.status === "done" && <CheckCircle weight="fill" size={16} className="text-teal-400"/>}
          </div>
        </div>
        <button onClick={onMenu} className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center" data-testid="walk-room-menu"><List size={22}/></button>
      </div>

      <div className="px-4">
        <button onClick={() => onOpenCamera(null)} data-testid="walk-take-photos"
          className="w-full h-[72px] rounded-2xl bg-teal-500 text-white font-black text-[22px] flex items-center justify-center gap-3 shadow-lg shadow-teal-500/25 active:scale-[0.99]">
          <Camera weight="fill" size={30}/> Take photos
        </button>

        {photos.length > 0 && (
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="walk-room-photos">
            {photos.slice(-12).map((p, i) => (
              <button key={p.path} onClick={() => setViewing(p)} data-testid={`walk-photo-thumb-${i}`}
                className="relative w-16 h-16 shrink-0 rounded-xl overflow-hidden border border-white/15">
                <img src={photoUrl(p)} alt="" className="w-full h-full object-cover" loading="lazy"/>
                {p.item && <span className="absolute bottom-0 inset-x-0 bg-black/60 text-[8px] px-1 truncate">{p.item}</span>}
                {p.callouts?.length > 0 && <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-600 text-[9px] font-black flex items-center justify-center">{p.callouts.length}</span>}
              </button>
            ))}
            <div className="shrink-0 self-center text-sm text-slate-400 pl-1">{photos.length} total</div>
          </div>
        )}

        {mode === "fast" ? (
          <div className="mt-4 space-y-3" data-testid="walk-fast-track">
            <div className="text-sm text-slate-300">Fast Track — one wide photo of the room, flag only problems.</div>
            <textarea value={flag} onChange={e => setFlag(e.target.value)} onBlur={() => onFastFlag(flag)} rows={3}
              placeholder="Problems? e.g. 'Blinds broken, hole in closet door' — leave blank if the room is fine" data-testid="walk-fast-flag"
              className="w-full rounded-2xl bg-[#13263a] border border-white/10 p-3 text-base placeholder-slate-500"/>
            {flag && staff && !room.fast_flag_wo_id && (
              <button onClick={() => onFindingWo(null)} className="h-11 px-4 rounded-xl bg-red-600/90 font-black text-sm flex items-center gap-2" data-testid="walk-fast-wo">
                <Wrench weight="fill" size={16}/> Make it a work order
              </button>
            )}
            {room.fast_flag_wo_id && <div className="text-xs text-teal-300 font-bold">Work order created for this flag.</div>}
          </div>
        ) : (
          <>
            <div className="mt-3 text-xs text-slate-400">Tap an item, then Good / Fair / Damage on the bar. Photo works for any rating; Fair or Damage requires one.</div>
            <div className="mt-2 grid grid-cols-2 gap-2.5" data-testid="walk-items">
              {room.items.map(it => (
                <Chip key={it.name} it={it} selected={sel === it.name} photos={itemPhotos(it.name)} needOverride={!hasEvidence(it.name)}
                  onTap={() => {
                    setSel(it.name);
                    if (it.appliance && staff) onNameplate(it.name);
                    else if (it.measurable && it.rating && it.rating !== "good") onMeasure(it.name);
                  }}/>
              ))}
            </div>
            {selItem && (
              <div className="mt-3 flex gap-2 flex-wrap" data-testid="walk-item-actions">
                <button onClick={() => onOpenCamera(sel)} className="h-11 px-4 rounded-xl bg-teal-500/20 text-teal-200 font-bold text-sm flex items-center gap-2" data-testid="walk-item-photo">
                  <Camera weight="fill" size={16}/> Photo of {sel}{itemPhotos(sel) ? ` (${itemPhotos(sel)})` : ""}
                </button>
                {selItem.measurable && (selItem.rating === "fair" || selItem.rating === "dmg") && <button onClick={() => onMeasure(sel)} className="h-11 px-4 rounded-xl bg-white/10 font-bold text-sm flex items-center gap-2"><Ruler size={16}/> Blinds size</button>}
                {staff && !selItem.wo_id && (selItem.rating === "fair" || selItem.rating === "dmg") && <button onClick={() => onFindingWo(sel)} className="h-11 px-4 rounded-xl bg-red-600/90 font-black text-sm flex items-center gap-2" data-testid="walk-item-wo"><Wrench weight="fill" size={16}/> Work order</button>}
                {selItem.wo_id && <span className="h-11 px-3 rounded-xl bg-teal-500/20 text-teal-300 text-xs font-bold flex items-center">WO created</span>}
              </div>
            )}
          </>
        )}
      </div>

      <div className="px-4 mt-4" data-testid="walk-room-notes">
        {!showNote ? (
          <button onClick={() => setShowNote(true)} className="h-11 px-4 rounded-xl bg-white/10 font-bold text-sm flex items-center gap-2" data-testid="walk-add-note">
            <NotePencil size={18} className="text-teal-300"/> Add room notes
          </button>
        ) : (
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-teal-400 flex items-center gap-1"><NotePencil size={12}/> Room notes</div>
            <textarea value={note} onChange={e => setNote(e.target.value)} onBlur={() => onNote(note)} rows={3} data-testid="walk-room-note"
              placeholder="Anything the office or turn crew should know about this room…"
              className="mt-1 w-full rounded-2xl bg-[#13263a] border border-white/10 p-3 text-base placeholder-slate-500"/>
          </div>
        )}
      </div>

      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="walk-photo-viewer" onClick={() => setViewing(null)}>
          <div className="flex items-center justify-between p-4 text-sm">
            <span className="text-slate-300">{viewing.item ? `${viewing.item} · ` : ""}{(viewing.taken_at || viewing.uploaded_at || "").slice(0, 16).replace("T", " ")}</span>
            <button className="h-10 px-4 rounded-full bg-white/15 font-bold" data-testid="walk-photo-viewer-close">Close</button>
          </div>
          <div className="flex-1 relative flex items-center justify-center min-h-0">
            <div className="relative inline-block">
              <img src={photoUrl(viewing)} alt="" className="max-h-[65vh] max-w-full object-contain"/>
              {(viewing.callouts || []).map(c => (
                <span key={c.n} className="absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-red-600 border-2 border-white text-xs font-black flex items-center justify-center" style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%` }}>{c.n}</span>
              ))}
            </div>
          </div>
          {(viewing.callouts || []).some(c => c.note) && (
            <div className="px-4 text-xs text-slate-200 space-y-0.5 max-h-24 overflow-auto" data-testid="viewer-callout-notes">
              {viewing.callouts.filter(c => c.note).map(c => <div key={c.n}><b className="text-red-400">{c.n}</b> {c.note}</div>)}
            </div>
          )}
          <div className="p-4 pb-8 flex gap-2">
            <button onClick={(e) => { e.stopPropagation(); setMarking(viewing); setViewing(null); }} data-testid="walk-photo-markup"
              className="flex-1 h-14 rounded-2xl bg-teal-500 text-slate-900 font-black text-lg flex items-center justify-center gap-2"><MapPin weight="fill" size={22}/> {viewing.callouts?.length ? "Edit call-outs" : "Mark up"}</button>
            <button onClick={(e) => { e.stopPropagation(); if (window.confirm("Delete this photo?")) { onDeletePhoto(viewing.path); setViewing(null); } }} data-testid="walk-photo-delete"
              className="w-14 h-14 rounded-2xl bg-red-600 font-black flex items-center justify-center"><Trash weight="fill" size={22}/></button>
          </div>
        </div>
      )}
      {marking && (
        <CalloutEditor photo={marking} onClose={() => setMarking(null)}
          onSave={(pins) => onCallouts(marking.path, pins)}/>
      )}

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 inset-x-0 bg-[#0e2236] border-t border-white/10 pb-[env(safe-area-inset-bottom)]" data-testid="walk-bottom-bar">
        <div className={`grid ${mode === "fast" ? "grid-cols-5" : "grid-cols-6"} divide-x divide-white/10`}>
          <button onClick={() => onOpenCamera(sel)} className="h-[76px] flex flex-col items-center justify-center gap-1 active:bg-white/5" data-testid="walk-bar-photo">
            <Camera weight="fill" size={28} className="text-teal-400"/><span className="text-xs font-bold">Photo</span>
          </button>
          {mode === "fast" ? (
            <button onClick={() => { onFastFlag(""); setFlag(""); onNext(); }} className="h-[76px] col-span-3 flex flex-col items-center justify-center gap-1 active:bg-white/5" data-testid="walk-bar-room-ok">
              <CheckCircle weight="fill" size={28} className="text-emerald-400"/><span className="text-xs font-bold">Room OK → next</span>
            </button>
          ) : (
            <>
              <button onClick={() => rate("good")} className="h-[76px] flex flex-col items-center justify-center gap-1 active:bg-white/5" data-testid="walk-bar-good">
                <CheckCircle weight="bold" size={28} className="text-emerald-400"/><span className="text-xs font-bold">Good</span>
              </button>
              <button onClick={() => rate("fair")} className="h-[76px] flex flex-col items-center justify-center gap-1 active:bg-white/5" data-testid="walk-bar-fair">
                <WarningCircle weight="fill" size={28} className="text-yellow-400"/><span className="text-xs font-bold">Fair</span>
              </button>
              <button onClick={() => rate("dmg")} className="h-[76px] flex flex-col items-center justify-center gap-1 active:bg-white/5" data-testid="walk-bar-damage">
                <Warning weight="fill" size={28} className="text-red-500"/><span className="text-xs font-bold">Damage</span>
              </button>
              <button onClick={() => rate("na")} className="h-[76px] flex flex-col items-center justify-center gap-1 active:bg-white/5" data-testid="walk-bar-na">
                <Prohibit weight="bold" size={28} className="text-slate-400"/><span className="text-xs font-bold">N/A</span>
              </button>
            </>
          )}
          <button onClick={next} className="h-[76px] flex flex-col items-center justify-center gap-1 active:bg-white/5" data-testid="walk-bar-next">
            <ArrowRight weight="bold" size={28} className="text-teal-400"/><span className="text-xs font-bold">{roomIdx + 1 === rooms.length ? "Finish" : "Next"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
